// Category-only enhancement. Liquid remains the authority for variants, order
// and counts; each request renders the same collection section as a normal URL.
export function normalizeCategoryPrice(value, precision) {
  const text = String(value).trim().replace(',', '.');
  if (!text) return '';
  if (!/^\d*(?:\.\d*)?$/.test(text) || !/\d/.test(text)) return null;
  const digits = Math.max(0, Math.min(4, Number(precision) || 0));
  const [whole = '0', fraction = ''] = text.split('.');
  const scale = 10n ** BigInt(digits);
  let minor = BigInt(whole || '0') * scale + BigInt((fraction + '0'.repeat(digits)).slice(0, digits) || '0');
  if (Number(fraction[digits] || 0) >= 5) minor += 1n;
  return digits ? `${minor / scale}.${String(minor % scale).padStart(digits, '0')}` : String(minor);
}

const fields = (form) => Array.from(form?.elements || []);
const snapshots = (form) => fields(form).map((field) => ({ field, value: field.defaultValue ?? field.value, checked: field.defaultChecked ?? field.checked }));
const updatePriceTrack = (range) => {
  const sliders = Array.from(range.querySelectorAll('input[type="range"]'));
  const maximum = Number(sliders[1]?.getAttribute('max') || 0);
  sliders.forEach((slider, index) => {
    const progress = maximum > 0 ? Number(slider.value) / maximum * 100 : (index ? 100 : 0);
    slider.parentElement.style.setProperty(index ? '--range-max' : '--range-min', `${progress}%`);
  });
};
const restore = (snapshot) => {
  snapshot.forEach(({ field, value, checked }) => { field.value = value; field.checked = checked; });
  // Restored slider values already encode blank/default text bounds. Refresh
  // presentation directly, without synthesizing changes or altering bounds.
  const ranges = new Set(snapshot.map(({ field }) => field.closest('[data-category-price]')).filter(Boolean));
  ranges.forEach(updatePriceTrack);
};

export class CategoryControls {
  constructor(section, { previewDelay = 180, onDestroy } = {}) {
    this.document = section.ownerDocument;
    this.window = this.document.defaultView;
    this.id = section.getAttribute('data-category-section-id') || section.getAttribute('data-category-section') || section.closest('.shopify-section').id.replace('shopify-section-', '');
    this.host = section.closest('.shopify-section') || section;
    this.appliedUrl = this.cleanUrl(new URL(this.window.location.href));
    this.previewDelay = previewDelay;
    this.onDestroy = onDestroy;
    this.updateSequence = this.previewSequence = 0;
    this.lifetime = new this.window.AbortController();
    this.window.addEventListener('popstate', () => this.update(new URL(this.window.location.href), 'pop'), { signal: this.lifetime.signal });
    this.bind(section);
  }

  cleanUrl(url) {
    const clean = new URL(url);
    clean.searchParams.delete('section_id');
    clean.searchParams.delete('page');
    return clean;
  }

  bind(section) {
    this.binding?.abort();
    this.resizeObserver?.disconnect();
    this.section = section;
    this.binding = new this.window.AbortController();
    this.drawer = section.querySelector('[data-category-drawer]') || Array.from(this.document.querySelectorAll('[data-category-drawer]')).find((drawer) => drawer.getAttribute('data-category-section-id') === this.id);
    const forms = new Set([...section.querySelectorAll('form[is="facet-form"]'), ...this.drawer?.querySelectorAll('form[is="facet-form"]') || []]);
    this.baselines = new Map(Array.from(forms).map((form) => [form, snapshots(form)]));
    const listen = (target, type, callback, capture = false) => target?.addEventListener(type, callback, { capture, signal: this.binding.signal });
    for (const root of [section, this.drawer].filter(Boolean)) {
      listen(root, 'category:change', (event) => this.onChange(event));
      listen(root, 'submit', (event) => this.onSubmit(event), true);
      listen(root, 'click', (event) => this.onClick(event), true);
      listen(root, 'change', (event) => this.syncPrice(event), true);
      listen(root, 'input', (event) => this.syncPrice(event), true);
    }
    // Native facet:sort-by and facet-link hand off here before their global fetch.
    listen(section, 'category:update', (event) => {
      event.preventDefault();
      this.update(event.detail.url);
    });
    listen(this.drawer, 'toggle', (event) => {
      if (event.target !== this.drawer) return;
      if (this.drawer.open) this.openDraft();
      else if (!this.applying) this.discardDraft();
    });
    listen(this.drawer, 'dialog:after-hide', () => {
      if (!this.applying) this.discardDraft();
      this.openingTrigger = null;
      const trigger = this.findTrigger(this.drawerTrigger);
      this.trigger = trigger;
      trigger?.focus({ preventScroll: true });
    });
    const toolbar = section.querySelector('[data-category-toolbar]');
    if (toolbar) {
      const measure = (entry) => {
        const size = entry?.borderBoxSize;
        const height = (Array.isArray(size) ? size[0] : size)?.blockSize || toolbar.getBoundingClientRect().height;
        section.style.setProperty('--ph-category-toolbar-height', `${Math.ceil(height)}px`);
      };
      measure();
      if (this.window.ResizeObserver) {
        this.resizeObserver = new this.window.ResizeObserver((entries) => measure(entries[0]));
        this.resizeObserver.observe(toolbar);
      }
    }
    this.setPreview('unavailable');
    if (this.drawer?.open) this.openDraft();
  }

  formUrl(form) {
    const url = new URL(this.appliedUrl);
    url.pathname = new URL(form.action, url).pathname;
    for (const key of [...url.searchParams.keys()]) if (key.startsWith('filter.') || key === 'sort_by') url.searchParams.delete(key);
    for (const [key, value] of new this.window.FormData(form)) {
      if (key === 'page' || key === 'section_id' || value === '') continue;
      let normalized = value;
      if (key === 'filter.v.price.gte' || key === 'filter.v.price.lte') {
        const input = fields(form).find((field) => field.name === key);
        normalized = normalizeCategoryPrice(value, this.precision(input));
        if (normalized === null) return null;
      }
      url.searchParams.append(key, normalized);
    }
    return this.cleanUrl(url);
  }

  onChange(event) {
    // A body-reparented drawer is bound directly and can also bubble to section.
    if (event.defaultPrevented) return;
    event.preventDefault();
    const form = event.detail.form;
    const url = this.formUrl(form);
    if (!url) return;
    if (this.drawer?.contains(form)) this.schedulePreview(url);
    else this.update(url);
  }

  onSubmit(event) {
    const form = event.target.closest('form[is="facet-form"]');
    if (!form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.drawer?.contains(form)) this.applyDraft();
    else {
      const url = this.formUrl(form);
      if (url) this.update(url);
    }
  }

  onClick(event) {
    const trigger = event.target.closest('[data-category-filter-trigger]');
    if (trigger) {
      this.drawerTrigger = this.triggerIdentity(trigger);
      this.openingTrigger = this.drawer?.open ? null : trigger;
      // Impact opens the native drawer after this capture listener returns.
      return;
    }
    const apply = event.target.closest('[data-category-apply]');
    const retry = event.target.closest('[data-category-retry]');
    const link = event.target.closest('a[is="facet-link"]');
    if (!apply && !retry && !link) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (apply) this.applyDraft();
    else if (retry && this.failed) this.update(this.failed.url, this.failed.mode);
    else if (link) {
      const url = this.cleanUrl(new URL(link.href));
      // Native remove/reset URLs omit preview, pricing and tracking context.
      for (const [key, value] of this.appliedUrl.searchParams) {
        if (!key.startsWith('filter.') && key !== 'sort_by' && !url.searchParams.has(key)) url.searchParams.append(key, value);
      }
      if (this.drawer?.contains(link)) {
        const form = this.drawer.querySelector('form[is="facet-form"]');
        restore(this.baselines.get(form) || []);
        for (const field of fields(form)) {
          if (!field.name.startsWith('filter.') && field.name !== 'sort_by') continue;
          const values = url.searchParams.getAll(field.name);
          if (field.type === 'checkbox' || field.type === 'radio') field.checked = values.includes(field.value);
          else field.value = values[0] || '';
        }
        this.schedulePreview(url);
      } else this.update(url);
    }
  }

  openDraft() {
    const focused = this.document.activeElement;
    const source = this.openingTrigger || (focused?.matches('[data-category-filter-trigger]') ? focused : null);
    this.openingTrigger = null;
    this.trigger = this.findTrigger(source ? this.triggerIdentity(source) : this.drawerTrigger);
    this.drawerTrigger = this.triggerIdentity(this.trigger);
    this.discardDraft();
    const count = this.resultCount(this.section);
    this.setPreview(count === null ? 'unavailable' : 'current', count);
  }

  triggerIdentity(trigger) {
    return trigger && { id: trigger.id, context: trigger.getAttribute('data-category-filter-trigger'), controls: trigger.getAttribute('aria-controls') };
  }

  findTrigger(identity) {
    const visible = Array.from(this.section.querySelectorAll('[data-category-filter-trigger]')).filter((trigger) => {
      if (!trigger.isConnected || trigger.disabled || trigger.closest('[inert]') || !trigger.getClientRects().length) return false;
      const visibility = this.window.getComputedStyle(trigger).visibility;
      return visibility !== 'hidden' && visibility !== 'collapse' && (!this.drawer || trigger.getAttribute('aria-controls') === this.drawer.id);
    });
    return (identity?.id && visible.find((trigger) => trigger.id === identity.id)) ||
      (identity && visible.find((trigger) => trigger.getAttribute('data-category-filter-trigger') === identity.context && trigger.getAttribute('aria-controls') === identity.controls)) || visible[0] || null;
  }

  discardDraft() {
    this.stopPreview();
    const form = this.drawer?.querySelector('form[is="facet-form"]');
    restore(this.baselines.get(form) || []);
    this.setPreview('unavailable');
  }

  stopPreview() {
    this.previewSequence += 1;
    this.window.clearTimeout(this.previewTimer);
    this.previewRequest?.abort();
  }

  schedulePreview(url) {
    this.stopPreview();
    this.setPreview('loading');
    const sequence = this.previewSequence;
    this.previewTimer = this.window.setTimeout(async () => {
      this.previewRequest = new this.window.AbortController();
      try {
        const section = await this.fetchSection(url, this.previewRequest.signal);
        if (sequence !== this.previewSequence || !this.drawer?.open) return;
        const count = this.resultCount(section);
        this.setPreview(count === null ? 'unavailable' : 'current', count);
      } catch (error) {
        if (sequence === this.previewSequence && error.name !== 'AbortError') this.setPreview('unavailable');
      }
    }, this.previewDelay);
  }

  plural(element, count) {
    const locale = this.document.documentElement.lang || this.document.documentElement.getAttribute('lang') || 'en';
    const category = new Intl.PluralRules(locale).select(count);
    const template = element?.getAttribute(`data-results-${category}`) || element?.getAttribute('data-results-other');
    return template?.replace(/__COUNT__|\{\{\s*count\s*\}\}/g, new Intl.NumberFormat(locale).format(count));
  }

  setPreview(state, count = null) {
    const button = this.drawer?.querySelector('[data-category-apply]');
    if (!button) return;
    button.setAttribute('data-category-preview-state', state);
    button.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    const generic = button.getAttribute('data-apply-label') || button.textContent;
    button.textContent = state === 'loading' ? button.getAttribute('data-loading-label') || generic : state === 'current' && count !== null ? this.plural(button, count) || generic : generic;
  }

  resultCount(section) {
    if (section.hasAttribute('data-category-results-limited') || section.querySelector('[data-category-results-limited]')) return null;
    const results = section.querySelector('.collection__results[data-category-results-count]') || section.querySelector('[data-category-results-count]');
    if (!results) return null;
    const count = Number(results.getAttribute('data-category-results-count'));
    return Number.isInteger(count) && count >= 0 ? count : null;
  }

  async applyDraft() {
    if (this.applying) return;
    const form = this.drawer?.querySelector('form[is="facet-form"]');
    const url = form && this.formUrl(form);
    if (!url) return;
    this.applying = true;
    this.stopPreview();
    // Let Impact finish reparenting and returning focus before replacing markup.
    await this.drawer.hide();
    if (!this.destroyed) await this.update(url);
    this.applying = false;
  }

  async fetchSection(url, signal) {
    const request = new URL(url);
    request.searchParams.set('section_id', this.id);
    const response = await this.window.fetch(request.toString(), { signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error('Category section request failed');
    const parsed = new this.window.DOMParser().parseFromString(await response.text(), 'text/html');
    const host = parsed.getElementById?.(`shopify-section-${this.id}`) || parsed.querySelector('.shopify-section');
    const section = host?.matches('[data-category-section]') ? host : host?.querySelector('[data-category-section]');
    if (!section || (section.getAttribute('data-category-section-id') || section.getAttribute('data-category-section')) !== this.id) throw new Error('Category section response is missing');
    return section;
  }

  viewState() {
    const focused = this.document.activeElement;
    const form = focused?.closest('form[is="facet-form"]');
    const sortPopover = focused?.closest('x-popover');
    const sortTrigger = sortPopover && Array.from(this.section.querySelectorAll('button[aria-controls]')).find((button) => button.closest('facet-sort-by') && button.getAttribute('aria-controls') === sortPopover.id);
    return {
      focus: focused && { id: focused.id, name: focused.name, value: focused.value, type: focused.type, context: form?.getAttribute('data-category-facet-form'), trigger: focused.hasAttribute('data-category-filter-trigger') ? this.triggerIdentity(focused) : null, sortControls: sortTrigger?.getAttribute('aria-controls') },
      open: Array.from(this.section.querySelectorAll('details, facet-dialog')).filter((item) => item.open).map((item) => item.id),
      x: this.window.scrollX, y: this.window.scrollY,
      scroll: this.section.querySelector('.collection__facets-scroller')?.scrollTop,
    };
  }

  restoreView(view) {
    for (const item of this.section.querySelectorAll('details, facet-dialog')) if (view.open.includes(item.id)) item.setAttribute('open', '');
    const candidates = Array.from(this.section.querySelectorAll('input, select, textarea, button, a, summary'));
    const focus = view.focus;
    const target = focus && (focus.sortControls ? candidates.find((item) => item.closest('facet-sort-by') && item.getAttribute('aria-controls') === focus.sortControls) : focus.trigger ? this.findTrigger(focus.trigger) : candidates.find((item) => focus.id && item.id === focus.id) || candidates.find((item) => item.name === focus.name && item.type === focus.type && item.value === focus.value && item.closest('form')?.getAttribute('data-category-facet-form') === focus.context));
    target?.focus({ preventScroll: true });
    const scroller = this.section.querySelector('.collection__facets-scroller');
    if (scroller && view.scroll !== undefined) scroller.scrollTop = view.scroll;
    this.window.scrollTo(view.x, view.y);
  }

  async update(requestedUrl, mode = 'push') {
    if (this.destroyed) return;
    const url = this.cleanUrl(requestedUrl);
    if (url.origin !== this.appliedUrl.origin || url.pathname !== this.appliedUrl.pathname) return;
    this.stopPreview();
    this.updateRequest?.abort();
    this.updateRequest = new this.window.AbortController();
    const sequence = ++this.updateSequence;
    const view = this.viewState();
    this.section.classList.add('is-loading');
    this.section.setAttribute('aria-busy', 'true');
    const errorRegion = this.section.querySelector('[data-category-error]');
    if (errorRegion) errorRegion.hidden = true;
    try {
      const next = await this.fetchSection(url, this.updateRequest.signal);
      if (sequence !== this.updateSequence || this.destroyed) return;
      const nextHost = next.closest('.shopify-section') || next;
      this.binding.abort();
      this.resizeObserver?.disconnect();
      if (this.drawer?.parentElement === this.document.body) {
        this.drawer._originalParentBeforeAppend = null;
        this.drawer.remove();
      }
      this.host.replaceChildren(...this.document.importNode(nextHost, true).childNodes);
      this.bind(this.host.matches('[data-category-section]') ? this.host : this.host.querySelector('[data-category-section]'));
      if (mode === 'pop') this.window.history.replaceState(this.window.history.state, '', url.toString());
      else if (url.toString() !== this.appliedUrl.toString()) this.window.history.pushState(this.window.history.state, '', url.toString());
      this.appliedUrl = url;
      this.failed = null;
      this.restoreView(view);
      const status = this.section.querySelector('[data-category-status]');
      const count = this.resultCount(this.section);
      if (status) status.textContent = count === null ? '' : this.plural(status, count) || new Intl.NumberFormat(this.document.documentElement.lang || 'en').format(count);
    } catch (error) {
      if (sequence !== this.updateSequence || this.destroyed || error.name === 'AbortError') return;
      this.failed = { url, mode };
      for (const snapshot of this.baselines.values()) restore(snapshot);
      const region = this.section.querySelector('[data-category-error]');
      if (region) region.hidden = false;
      if (mode === 'pop') this.window.history.replaceState(this.window.history.state, '', this.appliedUrl.toString());
    } finally {
      if (sequence === this.updateSequence && !this.destroyed) {
        this.section.classList.remove('is-loading');
        this.section.setAttribute('aria-busy', 'false');
      }
    }
  }

  precision(input) {
    const owner = input?.closest('[data-category-currency-precision]');
    return Number(owner?.getAttribute('data-category-currency-precision') ?? 2);
  }

  syncPrice(event) {
    const range = event.target.closest('[data-category-price]');
    if (!range || !event.target.matches('input')) return;
    const texts = Array.from(range.querySelectorAll('[data-category-price-input]'));
    const sliders = Array.from(range.querySelectorAll('input[type="range"]'));
    const index = event.target.type === 'range' ? sliders.indexOf(event.target) : texts.indexOf(event.target);
    if (index < 0 || (event.type === 'input' && event.target.type !== 'range')) return;
    const precision = this.precision(event.target);
    if (event.target.type === 'range') texts[index].value = event.target.value;
    const values = texts.map((input) => normalizeCategoryPrice(input.value, precision));
    if (values.some((value) => value === null)) return;
    const minimum = Number(sliders[0]?.getAttribute('min') || 0);
    const maximum = Number(sliders[1]?.getAttribute('max') || 0);
    const numbers = values.map((value, offset) => value === '' ? (offset ? maximum : minimum) : Math.min(maximum, Math.max(minimum, Number(value))));
    if (numbers[0] > numbers[1]) numbers[index] = numbers[1 - index];
    texts.forEach((input, offset) => { if (values[offset] !== '' || offset === index && event.target.type === 'range') input.value = normalizeCategoryPrice(String(numbers[offset]), precision); });
    sliders.forEach((slider, offset) => { slider.value = String(numbers[offset]); });
    updatePriceTrack(range);
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.stopPreview();
    this.updateRequest?.abort();
    this.lifetime.abort();
    this.binding.abort();
    this.resizeObserver?.disconnect();
    if (this.drawer?.parentElement === this.document.body) {
      this.drawer._originalParentBeforeAppend = null;
      this.drawer.remove();
    }
    this.onDestroy?.();
  }
}

export function mountCategoryControls(document) {
  const instances = new Map();
  const scan = () => {
    for (const [section, controller] of [...instances]) {
      if (!controller.section.isConnected) { controller.destroy(); instances.delete(section); }
      else if (controller.section !== section) { instances.delete(section); instances.set(controller.section, controller); }
    }
    for (const section of document.querySelectorAll('[data-category-section]')) {
      if (!instances.has(section)) instances.set(section, new CategoryControls(section));
    }
  };
  scan();
  // One observer covers section editor replacement as well as rendered markup.
  const observer = new document.defaultView.MutationObserver(scan);
  observer.observe(document.body, { childList: true, subtree: true });
  return () => { observer.disconnect(); for (const controller of instances.values()) controller.destroy(); instances.clear(); };
}

if (typeof document !== 'undefined' && !window.phCategoryControls) {
  window.phCategoryControls = mountCategoryControls(document);
}
